import React, { FC, MouseEventHandler, useEffect, useRef } from "react";
import styled from "styled-components";
import { Button, Text, tombac, useToasts } from "tombac";
import { WhatsNewIcon } from "tombac-icons";
import { LegolandLink, useLegoland } from "../LegolandContext";
import { useReleaseNotesQuery } from "../legolandApi";
import {
  useReleaseNotesStatusQuery,
  useReleaseNotificationMarkDismissedMutation,
} from "./releaseNotesHooks";

const ActionBar = styled.div`
  margin-top: ${tombac.space(1)};
`;

const ToastContent: FC<{
  linkComponent: LegolandLink;
  onRefreshClick: MouseEventHandler;
  onViewReleaseNotesClick: MouseEventHandler;
  releaseNotesUrl: string;
  showRefresh: boolean;
}> = ({
  linkComponent: Link,
  onRefreshClick,
  onViewReleaseNotesClick,
  releaseNotesUrl,
  showRefresh,
}) => {
  return (
    <>
      <Text>
        Check the release notes to learn more about the improvements and newest
        additions.
      </Text>
      <ActionBar>
        {showRefresh && (
          <Button $mr="1sp" onClick={onRefreshClick} size="xs">
            Refresh page
          </Button>
        )}
        <Button
          as={Link}
          href={releaseNotesUrl}
          onClick={onViewReleaseNotesClick}
          size="xs"
        >
          Learn what's new
        </Button>
      </ActionBar>
    </>
  );
};

export const NewReleaseNotification: FC = () => {
  const { Link, releaseNotesUrl } = useLegoland();
  const { addToast, dismissToast } = useToasts();

  if (!releaseNotesUrl) {
    throw new Error(
      "NewReleaseNotification component requires releaseNotesUrl prop to be set on LegolandApp."
    );
  }

  const rnQuery = useReleaseNotesQuery();
  const rnStatusQuery = useReleaseNotesStatusQuery();
  const { mutate: markDismissed } =
    useReleaseNotificationMarkDismissedMutation();

  const isDismissed = rnStatusQuery.data?.isDismissed;

  const prevIsDismissed = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    let updatedInBg = false;
    let shouldReload = false;
    let shouldRedirect: string | false = false;

    if (isDismissed) {
      dismissToast("legoland-sdk.new-release");
    }

    if (prevIsDismissed.current === true && isDismissed === false) {
      updatedInBg = true;
    }

    if (isDismissed !== undefined) {
      prevIsDismissed.current = isDismissed;
    }

    if (isDismissed === false) {
      addToast("New release!", "default", {
        autoDismiss: false,
        content: (
          <ToastContent
            linkComponent={Link}
            onRefreshClick={() => {
              dismissToast("legoland-sdk.new-release");
              shouldReload = true;
            }}
            onViewReleaseNotesClick={(e) => {
              dismissToast("legoland-sdk.new-release");
              if (updatedInBg) {
                e.preventDefault();
                shouldReload = updatedInBg;
                shouldRedirect = releaseNotesUrl;
              }
            }}
            releaseNotesUrl={releaseNotesUrl}
            showRefresh={updatedInBg}
          />
        ),
        icon: <WhatsNewIcon size="lg" />,
        id: "legoland-sdk.new-release",
        onDismiss() {
          markDismissed(
            { releaseNotes: rnQuery.data!.releaseNotes! },
            {
              onSettled() {
                if (shouldRedirect) {
                  if (
                    shouldReload &&
                    window.location.pathname === shouldRedirect
                  ) {
                    window.location.reload();
                  } else {
                    window.location.assign(shouldRedirect);
                  }
                } else if (shouldReload) {
                  window.location.reload();
                }
              },
            }
          );
        },
        placement: "bottom-right",
      });
    }
  }, [isDismissed]);

  return null;
};
